// Herramientas para modelar piezas en 3D: materiales compartidos, fusión de geometrías estáticas
// (pocas llamadas de dibujo por pieza), texturas de texto y halos de luz para LEDs.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

const matCache = new Map();

/** Material estándar compartido (no modificar: usar mat(...).clone() si hace falta animarlo). */
export function mat(color, { roughness = 0.6, metalness = 0, ...rest } = {}) {
  const key = JSON.stringify([color, roughness, metalness, rest]);
  let m = matCache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({ color, roughness, metalness, ...rest });
    matCache.set(key, m);
  }
  return m;
}

export const M = {
  get metal() { return mat('#c9ccd1', { roughness: 0.28, metalness: 1 }); },
  get steel() { return mat('#a9adb3', { roughness: 0.38, metalness: 0.9 }); },
  get gold() { return mat('#d9b25a', { roughness: 0.3, metalness: 1 }); },
  get tin() { return mat('#d0d3d6', { roughness: 0.42, metalness: 0.85 }); },
  get copper() { return mat('#c27a4a', { roughness: 0.35, metalness: 1 }); },
  get black() { return mat('#1b1c1f', { roughness: 0.55 }); },
  get blackPlastic() { return mat('#18191b', { roughness: 0.45 }); },
  get darkPlastic() { return mat('#2b2d31', { roughness: 0.55 }); },
  get hole() { return mat('#050506', { roughness: 0.9 }); },
  get chip() { return mat('#1d1d20', { roughness: 0.5 }); },
  get white() { return mat('#f1f0ea', { roughness: 0.6 }); },
  get offWhite() { return mat('#e8e4d8', { roughness: 0.7 }); },
  get ceramic() { return mat('#c8b48a', { roughness: 0.6 }); },
};

/**
 * Geometría base reutilizable por clave (caché global de todas las familias). Las claves libres llevan
 * prefijo de familia (`sns:domo`); las de las primitivas ya describen su forma (`box2,3,4`).
 * Queda marcada como compartida: disposeObject no la libera al borrar una pieza.
 */
const geoCache = new Map();
export function cachedGeo(key, make) {
  let g = geoCache.get(key);
  if (!g) {
    g = make();
    g.userData.shared = true;
    geoCache.set(key, g);
  }
  return g;
}

/** Caja de los objetos visibles (Box3.expandByObject también cuenta los ocultos). */
export function visibleBounds(roots, box = new THREE.Box3()) {
  const tmp = new THREE.Box3();
  for (const root of roots) {
    root.updateWorldMatrix(true, true);
    root.traverseVisible((o) => {
      const g = o.geometry;
      if (!g || !(o.isMesh || o.isLine || o.isPoints) || o.userData.noBounds) return;
      if (o.boundingBox !== undefined) {
        if (o.boundingBox === null) o.computeBoundingBox();
        tmp.copy(o.boundingBox);
      } else {
        if (!g.boundingBox) g.computeBoundingBox();
        tmp.copy(g.boundingBox);
      }
      box.union(tmp.applyMatrix4(o.matrixWorld));
    });
  }
  return box;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();

/**
 * Constructor de modelos: acumula geometrías estáticas por material y al final las fusiona.
 * Las piezas móviles (perillas, LEDs) se agregan como mallas aparte con mesh().
 */
export class Builder {
  constructor() {
    this.root = new THREE.Group();
    this.byMat = new Map();
  }
  add(geo, material, { x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1 } = {}) {
    let g = geo.index ? geo.toNonIndexed() : geo.clone();
    if (!g.attributes.uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((g.attributes.position.count) * 2), 2));
    for (const k of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(k)) g.deleteAttribute(k);
    g.clearGroups();
    _e.set(rx, ry, rz);
    _q.setFromEuler(_e);
    _p.set(x, y, z);
    _s.set(sx, sy, sz);
    _m.compose(_p, _q, _s);
    g.applyMatrix4(_m);
    let list = this.byMat.get(material);
    if (!list) {
      list = [];
      this.byMat.set(material, list);
    }
    list.push(g);
    return this;
  }
  /** Caja con base en y (no centrada en y). */
  box(w, h, d, material, x = 0, y = 0, z = 0, rot = {}) {
    return this.add(cachedGeo(`box${w},${h},${d}`, () => new THREE.BoxGeometry(w, h, d)), material, { x, y: y + h / 2, z, ...rot });
  }
  rbox(w, h, d, r, material, x = 0, y = 0, z = 0, rot = {}) {
    return this.add(cachedGeo(`rbox${w},${h},${d},${r}`, () => new RoundedBoxGeometry(w, h, d, 2, Math.min(r, w / 2, h / 2, d / 2))), material, { x, y: y + h / 2, z, ...rot });
  }
  /** Cilindro vertical con base en y. */
  cyl(rTop, rBottom, h, material, x = 0, y = 0, z = 0, seg = 20, rot = {}) {
    return this.add(cachedGeo(`cyl${rTop},${rBottom},${h},${seg}`, () => new THREE.CylinderGeometry(rTop, rBottom, h, seg)), material, { x, y: y + h / 2, z, ...rot });
  }
  /** Cilindro horizontal (eje X) centrado en (x, y, z). */
  cylX(r, len, material, x, y, z, seg = 16) {
    return this.add(cachedGeo(`cyl${r},${r},${len},${seg}`, () => new THREE.CylinderGeometry(r, r, len, seg)), material, { x, y, z, rz: Math.PI / 2 });
  }
  cylZ(r, len, material, x, y, z, seg = 16) {
    return this.add(cachedGeo(`cyl${r},${r},${len},${seg}`, () => new THREE.CylinderGeometry(r, r, len, seg)), material, { x, y, z, rx: Math.PI / 2 });
  }
  /** Tramo de alambre entre dos puntos. */
  wire(a, b, r, material, seg = 8) {
    const va = new THREE.Vector3(...a);
    const vb = new THREE.Vector3(...b);
    const len = va.distanceTo(vb);
    if (len < 1e-6) return this;
    const g = cachedGeo(`wire${r},${seg}`, () => new THREE.CylinderGeometry(r, r, 1, seg, 1, true));
    const mid = va.clone().add(vb).multiplyScalar(0.5);
    const dir = vb.clone().sub(va).normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    const e = new THREE.Euler().setFromQuaternion(q);
    return this.add(g, material, { x: mid.x, y: mid.y, z: mid.z, rx: e.x, ry: e.y, rz: e.z, sy: len });
  }
  /** Pata doblada: baja desde 'top' hasta el punto de inserción con un codo. */
  leg(points, r, material) {
    for (let i = 0; i < points.length - 1; i++) this.wire(points[i], points[i + 1], r, material);
    for (let i = 1; i < points.length - 1; i++) this.add(cachedGeo(`sph${r}`, () => new THREE.SphereGeometry(r, 8, 6)), material, { x: points[i][0], y: points[i][1], z: points[i][2] });
    return this;
  }
  /** Malla independiente (no se fusiona). */
  mesh(geo, material, { x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0 } = {}, parent = this.root) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z);
    m.rotation.set(rx, ry, rz);
    m.castShadow = true;
    m.receiveShadow = true;
    parent.add(m);
    return m;
  }
  build({ shadows = true } = {}) {
    for (const [material, list] of this.byMat) {
      const g = list.length === 1 ? list[0] : mergeGeometries(list, false);
      const m = new THREE.Mesh(g, material);
      m.castShadow = shadows;
      m.receiveShadow = true;
      this.root.add(m);
    }
    this.byMat.clear();
    return this.root;
  }
}

// Texturas ---------------------------------------------------------------------------------------

/** Lienzo para dibujar serigrafía; s = píxeles por mm. */
export function canvas(wmm, hmm, s = 16) {
  const c = document.createElement('canvas');
  c.width = Math.max(4, Math.round(wmm * s));
  c.height = Math.max(4, Math.round(hmm * s));
  const g = c.getContext('2d');
  g.scale(s, s);
  return { c, g, s };
}

export function texture(c, { srgb = true, aniso = 8 } = {}) {
  const t = new THREE.CanvasTexture(c);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = aniso;
  t.needsUpdate = true;
  return t;
}

/** Texto en mm sobre un lienzo escalado. */
export function text(g, str, x, y, { size = 1.6, color = '#ffffff', align = 'center', baseline = 'middle', weight = 600, rotate = 0, font = 'Arial, Helvetica, sans-serif' } = {}) {
  g.save();
  g.translate(x, y);
  if (rotate) g.rotate(rotate);
  g.fillStyle = color;
  g.font = `${weight} ${size}px ${font}`;
  g.textAlign = align;
  g.textBaseline = baseline;
  g.fillText(str, 0, 0);
  g.restore();
}

/** Plano horizontal con textura (para caras superiores con serigrafía). */
export function texturedTop(w, d, tex, { roughness = 0.6, metalness = 0, transparent = false } = {}) {
  const material = new THREE.MeshStandardMaterial({ map: tex, roughness, metalness, transparent });
  const geo = new THREE.PlaneGeometry(w, d);
  geo.rotateX(-Math.PI / 2);
  return new THREE.Mesh(geo, material);
}

// Halo de luz para LEDs ------------------------------------------------------------------------
let glowTex = null;
function glowTexture() {
  if (glowTex) return glowTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.18, 'rgba(255,255,255,0.75)');
  grad.addColorStop(0.45, 'rgba(255,255,255,0.18)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  glowTex = new THREE.CanvasTexture(c);
  glowTex.colorSpace = THREE.SRGBColorSpace;
  glowTex.userData.shared = true;
  return glowTex;
}

export function glow(color, size = 12) {
  const m = new THREE.SpriteMaterial({
    map: glowTexture(),
    color,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const s = new THREE.Sprite(m);
  s.scale.set(size, size, 1);
  s.renderOrder = 10;
  s.userData.noPick = true;
  s.userData.baseSize = size;
  return s;
}

/** Ajusta un halo a un brillo 0–1.5. */
export function setGlow(sprite, b) {
  sprite.material.opacity = Math.min(1, b * 0.85);
  const s = sprite.userData.baseSize * (0.6 + 0.5 * Math.min(1.5, b));
  sprite.scale.set(s, s, 1);
  sprite.visible = b > 0.01;
}

/** Material emisivo propio (LEDs, segmentos) con color base y color de brillo. */
export function emissiveMat(base, glowColor, { roughness = 0.25, transparent = false, opacity = 1 } = {}) {
  const m = new THREE.MeshStandardMaterial({ color: base, emissive: glowColor, emissiveIntensity: 0, roughness, metalness: 0, transparent, opacity });
  return m;
}

export function setEmissive(material, b, k = 12) {
  material.emissiveIntensity = b * k;
}

/** Contorno de una placa con esquinas redondeadas y chaflanes opcionales, como THREE.Shape. */
export function boardShape(w, d, { r = 1, chamfer = {} } = {}) {
  // Coordenadas de la forma: (x, -z) — ver extrudeBoard.
  const s = new THREE.Shape();
  const x0 = -w / 2;
  const x1 = w / 2;
  const y0 = -d / 2;
  const y1 = d / 2;
  const tr = chamfer.tr || 0; // arriba-derecha (z negativo)
  const br = chamfer.br || 0;
  s.moveTo(x0 + r, y0);
  if (br) {
    s.lineTo(x1 - br, y0);
    s.lineTo(x1, y0 + br);
  } else {
    s.lineTo(x1 - r, y0);
    s.quadraticCurveTo(x1, y0, x1, y0 + r);
  }
  if (tr) {
    s.lineTo(x1, y1 - tr);
    s.lineTo(x1 - tr, y1);
  } else {
    s.lineTo(x1, y1 - r);
    s.quadraticCurveTo(x1, y1, x1 - r, y1);
  }
  s.lineTo(x0 + r, y1);
  s.quadraticCurveTo(x0, y1, x0, y1 - r);
  s.lineTo(x0, y0 + r);
  s.quadraticCurveTo(x0, y0, x0 + r, y0);
  return s;
}

/**
 * Placa de circuito extruida (grosor hacia +Y desde y0) con textura en la cara superior.
 * holes: [[x, z, r]] agujeros de montaje.
 */
export function extrudeBoard(shape, thickness, topTex, edgeColor, { holes = [], w, d, y0 = 0, roughness = 0.55 } = {}) {
  for (const [hx, hz, hr] of holes) {
    const p = new THREE.Path();
    p.absarc(hx, -hz, hr, 0, Math.PI * 2, true);
    shape.holes.push(p);
  }
  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false, curveSegments: 16 });
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, y0, 0);
  if (topTex) {
    topTex.repeat.set(1 / w, 1 / d);
    topTex.offset.set(0.5, 0.5);
    topTex.wrapS = topTex.wrapT = THREE.ClampToEdgeWrapping;
  }
  const top = new THREE.MeshStandardMaterial({ map: topTex || null, color: topTex ? '#ffffff' : edgeColor, roughness, metalness: 0.05 });
  const edge = mat(edgeColor, { roughness: 0.7 });
  const mesh = new THREE.Mesh(geo, [top, edge]);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function disposeObject(obj) {
  obj.traverse((o) => {
    if (o.geometry && !o.geometry.userData.shared) o.geometry.dispose();
    if (o.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) {
        if (m.userData && m.userData.shared) continue;
        if ([...matCache.values()].includes(m)) continue;
        if (m.map && !m.map.userData.shared) m.map.dispose();
        m.dispose();
      }
    }
  });
}
